import { API_BASE_URL, getBaseHeaders } from './api.config';
import { throwApiError } from './api.error';
import { County, Location, PaginatedResponse } from './api.types';
import { authenticatedCall } from './auth';

export interface LocationSearchResponse {
  counties: County[];
  locations: PaginatedResponse<Location>;
}

export type UserLocationsResponse = PaginatedResponse<Location>;

export const searchLocations = async (
  page: number = 1,
  size: number = 20,
  countyId: string | null = null,
  name: string | null = null,
  accessToken: string | null = null
): Promise<LocationSearchResponse> => {
  const headers: Record<string, string> = {
    ...(getBaseHeaders() as Record<string, string>),
    'Content-Type': 'application/json'
  };

  if (accessToken) {
    headers['Authorization'] = `Bearer ${accessToken}`;
  }

  const response = await fetch(
    `${API_BASE_URL}/locations/search?page=${page}&size=${size}&sortField=NAME&order=ASC`,
    {
      method: 'POST',
      headers: headers,
      body: JSON.stringify({
        ids: [],
        name: name,
        countyId: countyId
      })
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const fetchUserLocations = async (
  userId: string,
  page: number = 1,
  size: number = 10
): Promise<UserLocationsResponse> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/user-locations/search?page=${page}&size=${size}&sortField=CREATED_AT&order=ASC`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        userId: userId
      })
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const searchUserLocations = async (
  userId: string,
  name: string,
  page: number = 1,
  size: number = 10
): Promise<UserLocationsResponse> => {
  const body: any = {
    ids: [],
    userId: userId
  };

  if (name) {
    body.name = name;
  }

  const response = await authenticatedCall(
    `${API_BASE_URL}/user-locations/search?page=${page}&size=${size}&sortField=CREATED_AT&order=ASC`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders(),
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(body)
    }
  );

  if (!response.ok) {
    await throwApiError(response);
  }

  return await response.json();
};

export const deleteUserLocation = async (
  locationId: string
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/user-locations/${locationId}`,
    {
      method: 'DELETE',
      headers: {
        ...getBaseHeaders()
      }
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const setDefaultLocation = async (
  locationId: string
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/user-locations/${locationId}/default`,
    {
      method: 'PUT',
      headers: {
        ...getBaseHeaders()
      }
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};

export const addUserLocation = async (
  locationId: string
): Promise<void> => {
  const response = await authenticatedCall(
    `${API_BASE_URL}/user-locations/${locationId}`,
    {
      method: 'POST',
      headers: {
        ...getBaseHeaders()
      },
      body: ''
    }
  );

  if (!response.ok && response.status !== 204) {
    await throwApiError(response);
  }
};
